import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { eachDayOfInterval, format, parseISO } from "date-fns";
import { ClipboardClockIcon, Loader2 } from "lucide-react";
import { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";

import UpdateTimeSheetLog from "@/v2/api/time-sheet/UpdateTimeSheetLog";
import FindOneTimeSheetLog, {
    TimeSheetDetails,
    TimesheetEntry,
    WorkLogs,
} from "@/v2/api/time-sheet/FindOneTimeSheetLog";

import { Card, CardContent } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableRow } from "@/components/ui/table";
import TimeSheetSaveBtn from "@/v2/components/pages/time-sheet/TimeSheetSaveBtn";
import FormPageHeading from "@/v2/components/ui/heading/FormPage";
import LoadingAnimation from "@/v2/components/ui/LoadingAnimation";
import { Button } from "@/components/ui/button";
import {
    Dialog,
    DialogClose,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Field, FieldGroup } from "@/components/ui/field";
import { Textarea } from "@/components/ui/textarea";
import SaveTimeSheet from "@/v2/api/time-sheet/SaveTimeSheet";

const STATUS_COLOR: Record<WorkLogs["status"], string> = {
    PRESENT: "bg-green-100 text-green-800 border-green-200",
    ABSENT: "bg-red-100 text-red-800 border-red-200",
    SICK_LEAVE: "bg-orange-100 text-orange-800 border-orange-200",
    HOLIDAY: "bg-yellow-100 text-yellow-800 border-yellow-200",
    NOT_MOBILIZED: "bg-gray-100 text-gray-500 border-gray-200",
    DAY_OFF: "bg-blue-100 text-blue-800 border-blue-200",
    RELEASED: "bg-slate-200 text-slate-600 border-slate-300",
    VACATION: "bg-pink-100 text-pink-800 border-pink-200",
    PUNCH_MISS: "bg-purple-100 text-purple-800 border-purple-200",
};

const STATUS_LABELS: Record<WorkLogs["status"], string> = {
    PRESENT: "Present",
    ABSENT: "Absent",
    SICK_LEAVE: "Sick Leave",
    HOLIDAY: "Holiday",
    NOT_MOBILIZED: "Not Mobilized",
    DAY_OFF: "Day Off",
    RELEASED: "Released",
    VACATION: "Vaction",
    PUNCH_MISS: "Punch Miss",
};

type SelectableStatus = Exclude<
    WorkLogs["status"],
    "NOT_MOBILIZED" | "RELEASED"
>;
const SELECTABLE_STATUSES: SelectableStatus[] = [
    "ABSENT",
    "SICK_LEAVE",
    "HOLIDAY",
    "DAY_OFF",
    "VACATION",
    "PUNCH_MISS",
];

const HOUR_OPTIONS = Array.from({ length: 32 }, (_, i) => (i + 1) * 0.5);

const MemoizedSelectOptions = memo(() => {
    return (
        <>
            <optgroup label="Hours" className="bg-white text-black">
                {HOUR_OPTIONS.map((val) => (
                    <option
                        key={val}
                        value={val}
                        className="bg-white text-black"
                    >
                        {val.toFixed(1)} Hours
                    </option>
                ))}
            </optgroup>
            <optgroup label="Status" className="bg-white text-black">
                {SELECTABLE_STATUSES.map((val) => (
                    <option
                        key={val}
                        value={val}
                        className="bg-white text-black"
                    >
                        {STATUS_LABELS[val]}
                    </option>
                ))}
            </optgroup>
        </>
    );
});

MemoizedSelectOptions.displayName = "MemoizedSelectOptions";

const MemoizedTimeSheetRow = memo(
    ({
        entry,
        timeSheetId,
    }: {
        entry: TimesheetEntry;
        timeSheetId: string;
    }) => {
        const queryClient = useQueryClient();

        const cellMutation = useMutation({
            mutationKey: ["time-sheets-logs", timeSheetId],
            mutationFn: UpdateTimeSheetLog,
            onMutate: async (variables) => {
                await queryClient.cancelQueries({
                    queryKey: ["time-sheets-logs", timeSheetId],
                });

                const previousData = queryClient.getQueryData([
                    "time-sheets-logs",
                    timeSheetId,
                ]);

                queryClient.setQueryData(
                    ["time-sheets-logs", timeSheetId],
                    (oldData: {
                        logs: TimesheetEntry[] | undefined;
                        details: TimeSheetDetails | undefined;
                    }) => {
                        if (!oldData) return oldData;

                        return {
                            ...oldData,
                            logs: oldData.logs?.map((e) => {
                                if (e.id !== entry.id) return e;

                                const updatedLogs = e.work_logs.map((log) => {
                                    if (log.id === variables.cellId) {
                                        return {
                                            ...log,
                                            hours: variables.hours,
                                            status: variables.status,
                                        };
                                    }
                                    return log;
                                });

                                const totalHours = updatedLogs.reduce(
                                    (sum, log) =>
                                        log.status === "PRESENT"
                                            ? sum + log.hours
                                            : sum,
                                    0,
                                );

                                return {
                                    ...e,
                                    work_logs: updatedLogs,
                                    total_hours: totalHours,
                                };
                            }),
                        };
                    },
                );

                return { previousData };
            },
            onSuccess: (updatedCell) => {
                queryClient.setQueryData(
                    ["time-sheets-logs", timeSheetId],
                    (oldData: {
                        logs: TimesheetEntry[] | undefined;
                        details: TimeSheetDetails | undefined;
                    }) => {
                        if (!oldData) return oldData;

                        return {
                            logs: oldData.logs?.map((entry) => {
                                const logIndex = entry.work_logs.findIndex(
                                    (l) => l.id === updatedCell.cellId,
                                );

                                if (logIndex === -1) return entry;

                                const updatedLogs = [...entry.work_logs];
                                updatedLogs[logIndex] = {
                                    ...updatedLogs[logIndex],
                                    hours: updatedCell.hours,
                                    status: updatedCell.status,
                                };

                                const totalHours = updatedLogs.reduce(
                                    (sum, log) =>
                                        log.status === "PRESENT"
                                            ? sum + log.hours
                                            : sum,
                                    0,
                                );

                                return {
                                    ...entry,
                                    work_logs: updatedLogs,
                                    total_hours: totalHours,
                                };
                            }),
                            details: oldData?.details,
                        };
                    },
                );
            },
            onError: (_, __, context) => {
                queryClient.setQueryData(
                    ["time-sheets-logs", timeSheetId],
                    context?.previousData,
                );
            },
        });

        const handleChange = useCallback(
            ({
                event,
                cellId,
            }: {
                event: React.ChangeEvent<HTMLSelectElement>;
                cellId: string;
            }) => {
                const rawValue = event.target.value;
                const numericValue = Number(rawValue);
                const isNumeric = !isNaN(numericValue);

                const hours = isNumeric ? numericValue : 0;
                const status = isNumeric ? "PRESENT" : rawValue;

                cellMutation.mutate({
                    cellId: cellId,
                    hours: hours,
                    status: status as WorkLogs["status"],
                });
            },
            [cellMutation],
        );

        return (
            <TableRow>
                <TableCell
                    className="sticky left-0 z-10 w-[240px] max-w-[240px] truncate whitespace-nowrap border-r bg-white p-0 px-4 font-medium"
                    title={entry.employee_name}
                >
                    <div>{entry.employee_name}</div>
                    <div className="text-sm text-gray-500">{entry.iqama}</div>
                </TableCell>
                {entry.work_logs?.map((log) => {
                    const isUpdating =
                        cellMutation.isPending &&
                        cellMutation.variables?.cellId === log.id;

                    const selectedValue =
                        log.status == "PRESENT"
                            ? log.hours.toString()
                            : log.status;

                    return (
                        <TableCell
                            key={log.id}
                            className="whitespace-nowrap border p-0 text-center"
                        >
                            {isUpdating ? (
                                <div className="flex items-center justify-center">
                                    <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                                </div>
                            ) : ["NOT_MOBILIZED", "RELEASED"].includes(
                                  log.status,
                              ) ? (
                                <p className="flex min-h-[60px] min-w-[120px] items-center justify-center bg-gray-500">
                                    {STATUS_LABELS[log.status]}
                                </p>
                            ) : (
                                <select
                                    id={log.id}
                                    defaultValue={selectedValue}
                                    onChange={(e) =>
                                        handleChange({
                                            event: e,
                                            cellId: log.id,
                                        })
                                    }
                                    className={`min-h-[60px] min-w-[120px] ${STATUS_COLOR[log.status]} `}
                                >
                                    <MemoizedSelectOptions />
                                </select>
                            )}
                        </TableCell>
                    );
                })}

                <TableCell className="sticky right-0 z-10 min-h-[60px] w-[120px] border-r bg-white text-center font-medium">
                    {entry.total_hours}
                </TableCell>
            </TableRow>
        );
    },
);

MemoizedTimeSheetRow.displayName = "MemoizedTimeSheetRow";

function MonthDays({
    endDate,
    startDate,
}: {
    startDate: string | undefined;
    endDate: string | undefined;
}) {
    if (!endDate || !startDate) {
        return null;
    }

    const start = parseISO(startDate);
    const end = parseISO(endDate);

    const dayRange = eachDayOfInterval({ start, end });
    const formattedDays = dayRange.map((date) => ({
        weekDay: format(date, "EEEE"),
        day: format(date, "dd"),
    }));

    return (
        <>
            {formattedDays.map((data, idx) => {
                return (
                    <TableCell
                        key={idx}
                        className={`sticky top-0 min-w-[120px] bg-gray-100 text-center ${data?.weekDay == "Friday" ? "border-yellow-200 bg-yellow-100 text-yellow-800" : ""}`}
                    >
                        <p>{data?.weekDay}</p>
                        <p>{data?.day}</p>
                    </TableCell>
                );
            })}
        </>
    );
}

const MemoizedHeaderRow = memo(
    ({ startDate, endDate }: { startDate: string; endDate: string }) => {
        return (
            <TableRow>
                <TableCell className="sticky left-0 top-0 z-20 min-w-[120px] border-r bg-gray-100 font-bold">
                    Employee Name
                </TableCell>

                <MonthDays startDate={startDate} endDate={endDate} />

                <TableCell className="sticky right-0 top-0 z-20 min-h-[60px] min-w-[80px] border-r bg-gray-100 text-center font-bold">
                    Total Hours
                </TableCell>
            </TableRow>
        );
    },
);

const OtherDeductions = memo(
    ({
        initialAmount,
        initialReason,
        onRegisterUpdate,
    }: {
        initialAmount: number;
        initialReason: string;
        onRegisterUpdate: (
            fn: (data: { amount: number; reason: string }) => void,
        ) => void;
    }) => {
        const [displayData, setDisplayData] = useState({
            amount: initialAmount,
            reason: initialReason,
        });

        // Register the update function with the parent on mount
        useEffect(() => {
            onRegisterUpdate((newData) => setDisplayData(newData));
        }, [onRegisterUpdate]);

        return (
            <>
                <div>
                    <p>
                        Other Deductions :{" "}
                        <span className="font-bold">
                            {displayData.amount.toFixed(2)} SAR
                        </span>
                    </p>
                </div>
                <div>
                    <p>Other Deduction Reason:</p>
                    <p className="text-gray-600">
                        {displayData.reason || "N/A"}
                    </p>
                </div>
            </>
        );
    },
);

function OtherDeductionBtn({
    initialAmount,
    initialReason,
    onSave,
    timeSheetId,
    timeSheetDetails,
}: {
    initialAmount: number;
    initialReason: string;
    onSave: (data: { amount: number; reason: string }) => void;
    timeSheetId: string;
    timeSheetDetails: TimeSheetDetails;
}) {
    const [amount, setAmount] = useState(initialAmount);
    const [reason, setReason] = useState(initialReason);

    const [open, setOpen] = useState(false);

    const { mutate, isPending } = useMutation({
        mutationKey: ["time-sheets-logs", timeSheetId],
        mutationFn: () =>
            SaveTimeSheet({
                timeSheetId: timeSheetId,
                data: {
                    title: timeSheetDetails.title,
                    status: "DRAFTED",
                    description: timeSheetDetails.description,
                    note: timeSheetDetails.note,
                    deduction_note: reason,
                    other_deductions: amount,
                },
            }),
        onSettled: () => {
            setOpen(false);
        },
    });

    const handleSave = () => {
        onSave({ amount, reason });
        mutate();
    };

    return (
        <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
                <Button variant="outline">Other Deduction</Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-sm">
                <DialogHeader>
                    <DialogTitle>Add Other Deductions</DialogTitle>
                    <DialogDescription>
                        Here You can add additional Deduction if occured
                    </DialogDescription>
                </DialogHeader>
                <FieldGroup>
                    <Field>
                        <Label htmlFor="deduction_amount">
                            Deduction Amount
                        </Label>
                        <Input
                            id="deduction_amount"
                            name="deduction_amount"
                            value={amount}
                            onChange={(e) => setAmount(Number(e.target.value))}
                        />
                    </Field>
                    <Field>
                        <Label htmlFor="deduction_reason">Deduction Note</Label>
                        <Textarea
                            id="deduction_reason"
                            name="deduction_reason"
                            value={reason}
                            onChange={(e) => setReason(e.target.value)}
                        />
                    </Field>
                </FieldGroup>
                <DialogFooter>
                    <DialogClose asChild>
                        <Button variant="outline">Cancel</Button>
                    </DialogClose>
                    <Button
                        onClick={handleSave}
                        disabled={isPending}
                        className="w-28"
                    >
                        {isPending ? <Loader2 /> : "Save Changes"}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

export default function ViewOneTimeSheetLog() {
    const params = useParams();
    const timeSheetId = params?.timeSheetId || "";

    const query = useQuery({
        queryKey: ["time-sheets-logs", timeSheetId],
        queryFn: () =>
            FindOneTimeSheetLog({
                timeSheetId: timeSheetId,
            }),
    });

    const currentMonth = useMemo(() => {
        const startDate = query?.data?.details?.start_date;
        if (startDate) {
            const date = parseISO(startDate);
            return format(date, "MMMM yyyy");
        }
    }, [query?.data?.details?.start_date]);

    const deductionAmountRef = useRef(0);
    const deductionReasonRef = useRef("");

    useEffect(() => {
        if (query.data?.details) {
            deductionAmountRef.current = query.data.details.other_deductions;
            deductionReasonRef.current = query.data.details.deduction_note;
        }
    }, [query.data]);

    const updateDisplayRef = useRef<
        ((data: { amount: number; reason: string }) => void) | null
    >(null);

    const handleCaptureDeductions = useCallback(
        (data: { amount: number; reason: string }) => {
            deductionAmountRef.current = data.amount;
            deductionReasonRef.current = data.reason;

            updateDisplayRef.current?.(data);
        },
        [],
    );

    if (query.isLoading || query.isFetching) {
        return <LoadingAnimation />;
    }

    if (!query.data) return <p>Something went wrong</p>;

    return (
        <>
            <Card>
                <FormPageHeading
                    title={"Edit Time Sheet "}
                    description="Edit time sheet for the mobilized employees"
                    Icon={ClipboardClockIcon}
                />
                <CardContent className="flex flex-col">
                    <h2 className="py-4 text-2xl font-bold">
                        Timesheet - {currentMonth}
                    </h2>
                    <div className="relative max-h-[480px] w-full overflow-auto">
                        <Table>
                            <TableBody>
                                <MemoizedHeaderRow
                                    startDate={query?.data?.details?.start_date}
                                    endDate={query?.data?.details?.end_date}
                                />
                                {query?.data?.logs?.map((entry) => (
                                    <MemoizedTimeSheetRow
                                        key={entry.id}
                                        entry={entry}
                                        timeSheetId={timeSheetId}
                                    />
                                ))}
                            </TableBody>
                        </Table>
                    </div>
                    <div className="flex items-start justify-between space-x-2 py-4">
                        <div className="max-w-[calc(100%-240px)]">
                            <OtherDeductions
                                initialAmount={
                                    query.data.details.other_deductions
                                }
                                initialReason={
                                    query.data.details.deduction_note
                                }
                                onRegisterUpdate={(fn) =>
                                    (updateDisplayRef.current = fn)
                                }
                            />
                        </div>

                        <div className="flex max-w-[240px] space-x-2">
                            <OtherDeductionBtn
                                initialAmount={
                                    query.data.details.other_deductions
                                }
                                initialReason={
                                    query.data.details.deduction_note
                                }
                                onSave={handleCaptureDeductions}
                                timeSheetId={timeSheetId}
                                timeSheetDetails={query?.data?.details}
                            />
                            <TimeSheetSaveBtn
                                timeSheetId={timeSheetId}
                                timeSheetDetails={query?.data?.details}
                                getDeductionData={() => ({
                                    other_deductions:
                                        deductionAmountRef.current,
                                    deduction_note: deductionReasonRef.current,
                                })}
                            />
                        </div>
                    </div>
                </CardContent>
            </Card>
        </>
    );
}
